import type { BillingState } from './useBillingState';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from 'hooks/useAuth';
import { useBillingState } from './useBillingState';
import { useCheckout } from './useCheckout';
import { useCheckoutReturn, useIsActivatingCheckout } from './useCheckoutReturn';
import { daysUntil, formatBillingDate } from './billing-format';

const DISMISS_KEY_PREFIX = 'PERTENTO-BILLING-BANNER-DISMISSED';

export type BannerAction = { label: string; onClick: () => void };

export type BannerModel = {
  tone: 'neutral' | 'destructive';
  message: string;
  action?: BannerAction;
  onDismiss?: () => void;
};

const readDismissed = (key: string) => {
  try {
    return !!sessionStorage.getItem(key);
  } catch {
    return false;
  }
};

const writeDismissed = (key: string) => {
  try {
    sessionStorage.setItem(key, 'true');
  } catch {
    // Storage blocked (private mode, site data cleared): the dismissal still
    // holds for this page through state, it just does not survive a reload.
  }
};

/**
 * Everything the ActivationBanner says, derived here so the component only
 * renders. The states follow docs/billing/ui.md's table, extended to every
 * status Polar has, so an unrecognised "bad" status collapses into the same
 * attention banner as past_due rather than going quiet.
 *
 * Claims about serving come from `billingActive` (computed server-side by the
 * same function the gate uses), never inferred from the status.
 *
 * Trial dismissal is scoped to the trial-end date, so a dismissed "13 days
 * left" does not suppress a later trial; it is kept in sessionStorage plus
 * state, so dismissing re-renders without a forced update.
 *
 * Also runs useCheckoutReturn: the banner is mounted for the whole dashboard
 * session, which is what a 30-second activation poll needs.
 */
export const useActivationBanner = () => {
  useCheckoutReturn();
  const isActivating = useIsActivatingCheckout();
  const { user } = useAuth();
  const { billingState, isLoading } = useBillingState();
  const { openBillingPortal } = useCheckout();
  const navigate = useNavigate();
  const [dismissedKeys, setDismissedKeys] = useState<string[]>([]);

  const canManageBilling = !!user && user.role !== 'Member';

  const trialDismissKey = `${DISMISS_KEY_PREFIX}-trialing-${(billingState && subjectOf(billingState).trialEnd) ?? ''}`;
  const isTrialDismissed = dismissedKeys.includes(trialDismissKey) || readDismissed(trialDismissKey);

  const dismissTrial = () => {
    writeDismissed(trialDismissKey);
    setDismissedKeys((keys) => [...keys, trialDismissKey]);
  };

  const banner =
    isLoading || !billingState
      ? null
      : deriveBanner({
          billingState,
          isActivating,
          isTrialDismissed,
          dismissTrial,
          now: Date.now(),
          // A checkout needs a plan (an agency: its fee), and the picker is on the billing page.
          checkoutAction: canManageBilling
            ? {
                label: billingState?.agencyFee ? 'Choose the agency fee' : 'Choose a plan',
                onClick: () => navigate('/settings/billing'),
              }
            : undefined,
          portalAction: canManageBilling ? { label: 'Manage billing', onClick: openBillingPortal } : undefined,
        });

  return { banner };
};

type DeriveBannerParams = {
  billingState: BillingState;
  isActivating: boolean;
  isTrialDismissed: boolean;
  dismissTrial: () => void;
  now: number;
  /** Undefined for a Member: checkout and the portal are Owner/Admin actions. */
  checkoutAction: BannerAction | undefined;
  portalAction: BannerAction | undefined;
};

const ASK_AN_ADMIN = ' Ask an account Owner or Admin to update billing.';

type Subject = {
  hasLiveSubscription: boolean;
  subscriptionStatus: string | null;
  trialEnd: string | null;
  trialAvailable: boolean;
  /** What is being bought, for the copy. */
  noun: string;
};

/**
 * What the banner is about: an account's plan, or, for an agency, its fee. An agency
 * has no sessions plan, so reading its (empty) plan state would tell a paying agency
 * to choose a plan. Everything that decides whether the account serves is the fee.
 */
const subjectOf = (billingState: BillingState): Subject =>
  billingState.agencyFee
    ? {
        hasLiveSubscription: billingState.agencyFee.live,
        subscriptionStatus: billingState.agencyFee.status,
        trialEnd: billingState.agencyFee.trialEnd,
        trialAvailable: billingState.agencyFeeTrialAvailable,
        noun: 'the agency fee',
      }
    : {
        hasLiveSubscription: billingState.hasLiveSubscription,
        subscriptionStatus: billingState.subscriptionStatus,
        trialEnd: billingState.trialEnd,
        trialAvailable: billingState.trialAvailable,
        noun: 'a plan',
      };

function deriveBanner(params: DeriveBannerParams): BannerModel | null {
  const { billingState, isActivating, isTrialDismissed, dismissTrial, now, checkoutAction, portalAction } = params;
  const { billingMode, billedBy } = billingState;
  const { hasLiveSubscription, subscriptionStatus, trialEnd, noun, trialAvailable } = subjectOf(billingState);
  const { graceEndsAt, billingActive } = billingState;

  if (billingMode !== 'card') return null;

  // A client of an agency has a plan of its own, but the agency buys and manages it: there is
  // nothing the client's users can do, so the banner names who can, and only speaks up when
  // serving is affected.
  if (billedBy && !billingState.isPayer) return deriveClientBanner({ billingState, agencyName: billedBy.name });

  if (isActivating && !hasLiveSubscription) {
    return { tone: 'neutral', message: 'Activating your subscription...' };
  }

  // Serving is on despite no subscription at all: internal traffic, which the
  // server exempts. Asking our own account to add a card would be noise.
  if (billingActive && !subscriptionStatus) return null;

  // With a live plan the way forward is always the portal; a checkout would be refused (409).
  const recovery = hasLiveSubscription ? portalAction : checkoutAction;
  const withRecovery = (message: string): BannerModel => ({
    tone: 'destructive',
    message: recovery ? message : `${message}${ASK_AN_ADMIN}`,
    action: recovery,
  });

  if (!subscriptionStatus) {
    return {
      tone: 'neutral',
      message: checkoutAction
        ? trialAvailable
          ? `Choose ${noun} to start your 12-day free trial. Nothing serves to your site yet, and the account is read-only until then.`
          : `Choose ${noun} to start serving again. Nothing serves to your site yet, and the account is read-only until then.`
        : `Nothing serves to your site yet. Ask an account Owner or Admin to choose ${noun}.`,
      action: checkoutAction,
    };
  }

  if (subscriptionStatus === 'active') return null;

  if (subscriptionStatus === 'trialing') {
    if (isTrialDismissed) return null;
    const remaining = daysUntil(trialEnd, now);
    const days = remaining === null ? 'A few days' : `${remaining} day${remaining === 1 ? '' : 's'}`;
    return {
      tone: 'neutral',
      message: `Trial: ${days} left.${trialEnd ? ` First charge ${formatBillingDate(trialEnd)}.` : ''}`,
      onDismiss: dismissTrial,
    };
  }

  if (subscriptionStatus === 'past_due') {
    return withRecovery(
      billingActive && graceEndsAt
        ? `Your last payment failed. Experiments and surveys stop serving on ${formatBillingDate(graceEndsAt)} unless the card is updated.`
        : 'Your last payment failed, so experiments and surveys have stopped serving and the account is read-only.',
    );
  }

  // unpaid, canceled, incomplete, incomplete_expired, paused: one attention
  // banner, not six silently different behaviours.
  return withRecovery(
    billingActive
      ? 'There is a problem with your subscription.'
      : 'There is a problem with your subscription, so experiments and surveys have stopped serving and the account is read-only.',
  );
}

type DeriveClientBannerParams = { billingState: BillingState; agencyName: string };

function deriveClientBanner({ billingState, agencyName }: DeriveClientBannerParams): BannerModel | null {
  const { billingActive, subscriptionStatus, graceEndsAt } = billingState;

  if (!subscriptionStatus && !billingActive) {
    return {
      tone: 'destructive',
      message: `${agencyName} has not chosen a plan for this account yet, so experiments and surveys are not serving and the account is read-only. Ask ${agencyName} to choose one.`,
    };
  }

  if (subscriptionStatus === 'past_due' && billingActive && graceEndsAt) {
    return {
      tone: 'destructive',
      message: `Billed through ${agencyName}. Their last payment failed, and experiments and surveys stop serving on ${formatBillingDate(graceEndsAt)} unless ${agencyName} updates the card.`,
    };
  }

  if (billingActive) return null;

  return {
    tone: 'destructive',
    message: `Billed through ${agencyName}. Experiments and surveys are not serving, and the account is read-only, until ${agencyName} has an active plan for this account.`,
  };
}
